import { useCallback, useEffect, useState } from "react";
import { useLocation, useSearch } from "wouter";

import { apiClient } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";

interface Session {
  id: string;
  device_name: string;
  status: string;
  verification_code: number;
  verification_phrase?: string;
  starts_at?: string;
  ends_at?: string;
  created_at: string;
}

interface Pagination {
  page: number;
  per_page: number;
  total: number;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function formatDuration(startsAt?: string, endsAt?: string): string {
  if (!startsAt || !endsAt) return "\u2014";
  const ms = new Date(endsAt).getTime() - new Date(startsAt).getTime();
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem > 0 ? `${hours}h ${rem}m` : `${hours}h`;
}

function statusBadge(status: string) {
  switch (status) {
    case "active":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-green-500/20 text-green-400">
          Active
        </span>
      );
    case "expired":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          Expired
        </span>
      );
    case "cancelled":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-red-500/20 text-red-400">
          Cancelled
        </span>
      );
    case "awaiting_payment":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-amber-500/20 text-amber-400">
          Awaiting Payment
        </span>
      );
    default:
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      );
  }
}

export function Sessions() {
  const { selectedVenue } = useAuth();
  const search = useSearch();
  const [, navigate] = useLocation();
  const params = new URLSearchParams(search);
  const page = Math.max(1, parseInt(params.get("page") ?? "1", 10) || 1);

  const [sessions, setSessions] = useState<Session[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSessions = useCallback(
    async (p: number) => {
      if (!selectedVenue) return;
      setLoading(true);
      try {
        const res = await apiClient["/api/v1/venue/sessions"]["get"]({
          parameters: { venue_id: selectedVenue.id, page: p, per_page: 25 },
          body: null,
        });
        if (res.code === 200) {
          const body = res.body as { data: Session[]; pagination: Pagination };
          setSessions(body.data);
          setPagination(body.pagination);
        }
      } catch {
        // Ignore
      } finally {
        setLoading(false);
      }
    },
    [selectedVenue],
  );

  useEffect(() => {
    fetchSessions(page);
  }, [fetchSessions, page]);

  const totalPages = pagination ? Math.max(1, Math.ceil(pagination.total / pagination.per_page)) : 1;

  const goToPage = (p: number) => {
    navigate(p === 1 ? "/sessions" : `/sessions?page=${p}`);
  };

  return (
    <main className="p-6">
      <h1 className="text-2xl font-bold text-text-primary mb-6">Sessions</h1>

      {loading && <p className="text-text-muted text-sm">Loading sessions...</p>}

      {!loading && sessions.length === 0 && (
        <div className="bg-surface-raised border border-border rounded-lg p-6 text-center">
          <p className="text-text-secondary">No sessions yet.</p>
        </div>
      )}

      {!loading && sessions.length > 0 && (
        <>
          <div className="grid gap-3">
            {sessions.map((s) => (
              <div
                key={s.id}
                className="bg-surface-raised border border-border rounded-lg p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div>
                    <div className="font-medium text-text-primary">{s.device_name}</div>
                    <div className="text-sm text-text-muted">
                      {s.starts_at ? formatTime(s.starts_at) : formatTime(s.created_at)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {statusBadge(s.status)}
                  <span className="text-sm text-text-secondary w-20 text-right">
                    {s.starts_at ? formatTime(s.starts_at) : "\u2014"}
                  </span>
                  <span className="text-sm text-text-secondary w-20 text-right">
                    {s.ends_at ? formatTime(s.ends_at) : "\u2014"}
                  </span>
                  <span className="text-sm text-text-muted w-16 text-right">
                    {formatDuration(s.starts_at, s.ends_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                className="px-4 py-2 text-sm rounded-md border border-border text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-text-muted">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
                className="px-4 py-2 text-sm rounded-md border border-border text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
}
